import api from './api';
import ENDPOINTS from './endpoints';

export const fetchOrders = async (status = null, page = 1, search = null) => {
  let url = ENDPOINTS.ORDERS.LIST;
  const params = new URLSearchParams();

  if (status) {
    if (Array.isArray(status)) {
      status.forEach((s) => params.append('status', s));
    } else {
      params.append('status', status);
    }
  }
  if (page > 1) {
    params.append('page', page.toString());
  }
  if (search && search.trim()) {
    params.append('search', search.trim());
  }

  const queryString = params.toString();
  if (queryString) {
    url = `${url}?${queryString}`;
  }

  const response = await api.get(url);
  const data = response.data.data || {};

  return {
    results: data.results || [],
    counts: data.counts || {},
    count: data.count || 0,
    next: data.next || null,
    previous: data.previous || null,
  };
};

export const fetchOrderDetails = async (orderId) => {
  const url = ENDPOINTS.ORDERS.DETAILS(orderId);
  const response = await api.get(url);
  return response.data.data || {};
};

export const approveOrder = async (orderId, options = {}) => {
  try {
    const url = ENDPOINTS.ORDERS.APPROVE(orderId);
    const normalizedProductIds = Array.isArray(options?.productIds)
      ? options.productIds
          .map((id) => String(id || '').trim())
          .filter((id) => id.length > 0)
      : [];
    const payload = { approved: true };
    if (normalizedProductIds.length > 0) {
      payload.product_ids = normalizedProductIds;
    }
    if (options?.initiatePayment === false) {
      payload.initiate_payment = false;
    }
    const response = await api.patch(url, payload);
    const responseData = response.data;

    if (responseData.error) {
      throw new Error(responseData.error);
    }

    return responseData;
  } catch (error) {
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }

    if (error instanceof Error) {
      throw error;
    }
    throw error;
  }
};

export const fetchOrderImages = async (orderId) => {
  try {
    const url = ENDPOINTS.ORDERS.IMAGES(orderId);
    const response = await api.get(url);
    const responseData = response.data;
    
    if (responseData.success && responseData.data && responseData.data.images) {
      return responseData.data.images || [];
    }
    
    return [];
  } catch (error) {
    // Silently fail - return empty array if images can't be fetched
    return [];
  }
};

export const fetchOrderPaymentStatus = async (orderId) => {
  try {
    const url = ENDPOINTS.ORDERS.PAYMENT_STATUS(orderId);
    const response = await api.get(url);
    const responseData = response.data;
    
    if (responseData.success && responseData.data) {
      return responseData.data;
    }
    
    throw new Error(responseData.message || 'Failed to fetch payment status');
  } catch (error) {
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to fetch payment status');
  }
};

export const downloadOrderPDF = async (orderId, invoiceType = 'client') => {
  try {
    const url = ENDPOINTS.ORDERS.PDF;
    const response = await api.post(
      url,
      { order_id: orderId, invoice_type: invoiceType },
      {
        responseType: 'blob',
      }
    );

    const contentDisposition = response.headers['content-disposition'];
    let filename = invoiceType === 'purchasing' 
      ? `purchasing_${orderId}.pdf`
      : `order_${orderId}.pdf`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url_blob = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url_blob;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url_blob);

    return true;
  } catch (error) {
    if (error.response?.data && error.response.data instanceof Blob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const errorData = JSON.parse(reader.result);
            reject(
              new Error(
                errorData.error || errorData.message || 'Failed to download PDF'
              )
            );
          } catch (e) {
            reject(new Error('Failed to download PDF'));
          }
        };
        reader.onerror = () => {
          reject(new Error('Failed to download PDF'));
        };
        reader.readAsText(error.response.data);
      });
    } else {
      throw new Error(error.message || 'Failed to download PDF');
    }
  }
};
